import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", assigned_staff_ids: [] });

  const load = async () => {
    try {
      const [p, u] = await Promise.all([api.get("/projects"), api.get("/users")]);
      setProjects(p.data);
      setStaff(u.data.filter((x) => x.role === "staff"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setEditing(null);
    setForm({ name: "", description: "", assigned_staff_ids: [] });
    setOpen(true);
  };
  const startEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      description: p.description,
      assigned_staff_ids: p.assigned_staff_ids || [],
    });
    setOpen(true);
  };

  const toggleStaff = (id) => {
    setForm((f) => ({
      ...f,
      assigned_staff_ids: f.assigned_staff_ids.includes(id)
        ? f.assigned_staff_ids.filter((x) => x !== id)
        : [...f.assigned_staff_ids, id],
    }));
  };

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/projects/${editing.id}`, form);
        toast.success("Project updated");
      } else {
        await api.post("/projects", form);
        toast.success("Project created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const staffName = (id) => staff.find((s) => s.id === id)?.name || "—";

  return (
    <div className="p-6 md:p-10 max-w-[1200px]">
      <div className="flex justify-between items-end mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Projects</div>
          <h1 className="font-display font-black text-3xl md:text-5xl leading-none">
            Assignments.
          </h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={startNew}
              className="rounded-sm bg-[#0033CC] hover:bg-[#002299] text-white"
              data-testid="new-project-btn"
            >
              <Plus size={16} className="mr-1" /> New project
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-md max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display">
                {editing ? "Edit project" : "New project"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider">Name</Label>
                <Input
                  className="mt-2 rounded-sm"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="project-name-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Description</Label>
                <Textarea
                  className="mt-2 rounded-sm"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="project-description-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Assign staff</Label>
                <div className="mt-2 border border-[#E5E5E5] rounded-sm p-3 space-y-2 max-h-52 overflow-y-auto">
                  {staff.length === 0 ? (
                    <div className="text-xs text-neutral-500">No staff added yet.</div>
                  ) : (
                    staff.map((s) => (
                      <label
                        key={s.id}
                        className="flex items-center gap-3 text-sm cursor-pointer"
                      >
                        <Checkbox
                          checked={form.assigned_staff_ids.includes(s.id)}
                          onCheckedChange={() => toggleStaff(s.id)}
                          data-testid={`assign-staff-${s.id}`}
                        />
                        <span>{s.name}</span>
                        <span className="text-xs text-neutral-500">{s.email}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                className="rounded-sm"
                data-testid="cancel-project-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={save}
                className="rounded-sm bg-[#0033CC] hover:bg-[#002299] text-white"
                data-testid="save-project-btn"
              >
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F3F2]">
            <tr className="text-left text-xs uppercase tracking-wider text-neutral-500">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Description</th>
              <th className="px-4 py-3">Assigned</th>
              <th className="px-4 py-3 w-32" />
            </tr>
          </thead>
          <tbody>
            {projects.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-neutral-500">
                  No projects yet.
                </td>
              </tr>
            ) : (
              projects.map((p) => (
                <tr key={p.id} className="border-t border-[#E5E5E5]" data-testid={`project-row-${p.id}`}>
                  <td className="px-4 py-3 font-medium">{p.name}</td>
                  <td className="px-4 py-3 text-neutral-600 max-w-md truncate">
                    {p.description || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(p.assigned_staff_ids || []).map((id) => (
                        <span
                          key={id}
                          className="text-[11px] uppercase tracking-wider bg-neutral-100 px-2 py-1 rounded-sm"
                        >
                          {staffName(id)}
                        </span>
                      ))}
                      {(p.assigned_staff_ids || []).length === 0 && (
                        <span className="text-xs text-neutral-500">Unassigned</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => startEdit(p)}
                        className="text-neutral-500 hover:text-[#0033CC] transition-colors"
                        data-testid={`edit-project-btn-${p.id}`}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => remove(p.id)}
                        className="text-neutral-500 hover:text-[#D82C0D] transition-colors"
                        data-testid={`delete-project-btn-${p.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
